import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { 
  fetchInvoiceSummaryAsync,
  clearError, 
  clearMessage,
  clearSummaryData
} from '../features/invoicesummary/invoicesummary.slice';
import type { RootState, AppDispatch } from '../store';

export const useInvoiceSummary = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  const {
    summaryData,
    loading,
    error,
    message
  } = useSelector((state: RootState) => state.invoiceSummary);
  const isSwedish = new URLSearchParams(window.location.search).get('lang') === 'sv-se';

  const fetchSummary = useCallback(() => {
    return dispatch(fetchInvoiceSummaryAsync());
  }, [dispatch]);

  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleClearMessage = useCallback(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const handleClearSummaryData = useCallback(() => {
    dispatch(clearSummaryData());
  }, [dispatch]);

  const refreshSummary = useCallback(() => {
    return dispatch(fetchInvoiceSummaryAsync());
  }, [dispatch]);

  const formatAmount = (num: number) => {
    return new Intl.NumberFormat(isSwedish ? 'sv-SE' : 'en-US').format(num);
};

  const calculateRemainingAmount = useCallback((): number => {
    if (!summaryData?.response) return 0;
    const { totalInvoiceAmount, paidInvoicesAmount, overdueInvoicesAmount } = summaryData.response;
    return totalInvoiceAmount - paidInvoicesAmount - overdueInvoicesAmount;
  }, [summaryData]);

  const calculatePaidPercentage = useCallback((): number => {
    if (!summaryData?.response || summaryData.response.totalInvoiceAmount === 0) return 0;
    return (summaryData.response.paidInvoicesAmount / summaryData.response.totalInvoiceAmount) * 100;
  }, [summaryData]);

  const calculateOverduePercentage = useCallback((): number => {
    if (!summaryData?.response || summaryData.response.totalInvoiceAmount === 0) return 0;
    return (summaryData.response.overdueInvoicesAmount / summaryData.response.totalInvoiceAmount) * 100;
  }, [summaryData]);

  return {
    summaryData,
    loading,
    error,
    message,
    fetchSummary,
    refreshSummary,
    clearError: handleClearError,
    clearMessage: handleClearMessage,
    clearSummaryData: handleClearSummaryData,
    formatAmount,
    calculateRemainingAmount,
    calculatePaidPercentage,
    calculateOverduePercentage,
    hasData: !!summaryData?.response,
    isEmpty: !summaryData?.response,
    totalAmount: summaryData?.response?.totalInvoiceAmount || 0,
    paidAmount: summaryData?.response?.paidInvoicesAmount || 0,
    overdueAmount: summaryData?.response?.overdueInvoicesAmount || 0,
    remainingAmount: calculateRemainingAmount(),
    paidPercentage: calculatePaidPercentage(),
    overduePercentage: calculateOverduePercentage(),
  };
};